import axios from "axios";

// Automatically detect environment and set appropriate URL
const getApiUrl = () => {
  // Check if we're in development mode
  if (process.env.NODE_ENV === 'development' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1') {
    return 'http://localhost:5002';
  }

  if (window.location.hostname === 'au.bookedsolid.health') {
    return 'https://au.api.bookedsolid.health';
  }

  if (window.location.hostname === 'dashboard.bookedsolid.co.uk') {
    return 'https://v2.api.bookedsolid.co.uk';
  }

  // Production URL
  return "https://v2.api.bookedsolid.health";
};

const url = getApiUrl();
let client_id = localStorage.client_id || '';

// All requests send cookies automatically (httpOnly session tokens)
axios.defaults.withCredentials = true;

// Modify alert
window.alert = function (message) {
  const alert = document.getElementById('alert');
  const hide = () => alert.style.display = 'none';
  document.getElementById('alert-text').innerText = message;
  alert.style.display = 'block';
  document.addEventListener("keyup", function (event) {
    if (event.keyCode === 13) hide();
  });
  setTimeout(hide, 6000);
}

/***********  Admin account functions ***********/

// Login a user
async function login(email, password, trustToken = null) {
  const data = {
    email,
    password,
  };

  // Add 2FA parameters if provided
  if (trustToken) data.trustToken = trustToken;

  // send request to server
  const response = await axios.post(`${url}/login/`, data)
    .catch((err) => { alert(err?.response?.data?.message || 'Incorrect email or password'); });
  console.log(response.data)
  if (response?.status == 200) {
    // Check if 2FA is required
    if (response.data.requiresTwoFA) {
      return response.data; // Return the 2FA requirement response
    }

    // if successful, store non-sensitive user data
    localStorage.name = response.data.name || '';
    localStorage.email = response.data.email;
    if (response.data.isv) {
      localStorage.isv_id = response.data.isv._id;
      const selectedAccount = response.data.isv.subaccounts[0];
      localStorage.client_id = selectedAccount._id;
      localStorage.isAdmin = true;
    } else {
      localStorage.client_id = response.data.client_id;
      client_id = response.data.client_id;
      localStorage.isAdmin = response.data.isAdmin || false;
      localStorage.removeItem('isv_id');
    }
    localStorage.registrationDate = response.data.registrationDate;
    window.dispatchEvent(new Event('settings_updated'));
    if (window.innerWidth < 768) window.location.href = "/calls";
    else window.location.href = "/";
  } else {
    return false;
  }
}

// Get an ISV account
async function getISV() {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.get(`/isv`);
  console.log(response);
  if (response.data.success === false) alert(response.data.message);
  else return response.data.user;
}

// Create a client account under an ISV
async function addClient(data) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/isv/client`, data);
  console.log(response);
  if (response.data.success === false) {
    alert(response.data.message);
    return false
  } else return response.data;
}

// Login a user
async function register(name, email, password, integration, practitioners, referralSource, acceptNewsletter, referrer, phone = '', attribution = {}) {
  const data = {
    name,
    email,
    password,
    integration,
    practitioners,
    referralSource,
    acceptNewsletter,
    referrer,
    phone,
    // A4: persist ad-click IDs against the account record for server-side attribution
    attribution,
  };
  // send request to server
  const response = await axios.post(`${url}/client/register`, data)
    .catch((err) => { alert(err?.response?.data?.message || 'Registration failed. Please try again later, or email contact@bookedsolid.co.uk'); });
  if (response?.status == 200) {
    // if successful, store non-sensitive user data
    localStorage.name = response.data.resName;
    localStorage.email = response.data.resEmail;
    localStorage.client_id = response.data.client_id;
    client_id = response.data.client_id;
    window.dispatchEvent(new Event('settings_updated'));
    return true;
  } else {
    return false;
  }
}

async function getReferralSourcePrompt() {
  const response = await axios.get(`${url}/client/referral-source-prompt`);
  return response.data;
}

async function updateReferralSourcePrompt(referralSource) {
  const response = await axios.put(`${url}/client/referral-source-prompt`, { referralSource });
  return response.data;
}

async function sendFeedback(feedback) {
  const response = await axios.post(`${url}/client/feedback`, { feedback });
  return response.data;
}

// Request the resetting of a password
async function forgotPassword(email) {
  if (!email) alert('Please enter your email');
  else {
    const data = {
      email
    };
    // send request to server
    const response = await axios.post(`${url}/client/forgot_password`, data);
    console.log(response);
  }
}

// Reset the password
async function resetPassword(token, new_password, confirm_password) {
  const data = {
    new_password,
    confirm_password
  };
  const response = await axios.post(`${url}/client/reset_password/${token}`, data)
    .catch((err) => { return err.response.data; });
  if (response) return response.data;
}

// Get client settings
async function getClientSettings() {
  if (!client_id) return false;
  const response = await axios.get(`${url}/client/settings/${client_id}`);
  return response?.data?.settings || false;
}

// Get default compliance preset text
async function getCompliancePreset() {
  if (!client_id) return false;
  const response = await axios.get(`${url}/client/compliance-preset/${client_id}`);
  return response?.data?.defaultComplianceText || '';
}

// Get client settings
async function getOutboundStats() {
  const response = await axios.get(`${url}/outbound/`);
  if (response?.data?.success === false) {
    alert(response.data.message);
    return false
  }
  return response?.data || false;
}

// Get outbound instances
async function getOutboundInstances() {
  if (!client_id) return [];
  const response = await axios.get(`${url}/outbound/instances/${client_id}`);
  if (response?.data?.success === false) {
    alert(response.data.message);
    return [];
  }
  return response?.data || [];
}

// Create outbound instance
async function createOutboundInstance(instanceData) {
  if (!client_id) return false;
  const response = await axios.post(`${url}/outbound/instances/${client_id}`, instanceData);
  if (response?.data?.success === false) {
    alert(response.data.message);
    return false;
  }
  return response?.data || false;
}

// Update outbound instance
async function updateOutboundInstance(instanceId, instanceData) {
  if (!client_id) return false;
  const response = await axios.put(`${url}/outbound/instances/${client_id}/${instanceId}`, instanceData);
  if (response?.data?.success === false) {
    alert(response.data.message);
    return false;
  }
  return response?.data || false;
}

// Delete outbound instance
async function deleteOutboundInstance(instanceId) {
  if (!client_id) return false;
  const response = await axios.delete(`${url}/outbound/instances/${client_id}/${instanceId}`);
  if (response?.data?.success === false) {
    alert(response.data.message);
    return false;
  }
  return response?.data || false;
}

// Get all campaign templates
async function getCampaignTemplates() {
  if (!client_id) return [];
  const response = await axios.get(`${url}/outbound/campaign-templates`);
  if (response?.data?.success === false) {
    alert(response.data.message);
    return [];
  }
  return response?.data || [];
}

// Create campaign template
async function createCampaignTemplate(templateData) {
  if (!client_id) return false;
  const response = await axios.post(`${url}/outbound/campaign-templates`, templateData);
  if (response?.data?.success === false) {
    alert(response.data.message);
    return false;
  }
  return response?.data || false;
}

// Update campaign template
async function updateCampaignTemplate(templateId, templateData) {
  if (!client_id) return false;
  const response = await axios.put(`${url}/outbound/campaign-templates/${templateId}`, templateData);
  if (response?.data?.success === false) {
    alert(response.data.message);
    return false;
  }
  return response?.data || false;
}

// Delete campaign template
async function deleteCampaignTemplate(templateId) {
  if (!client_id) return false;
  const response = await axios.delete(`${url}/outbound/campaign-templates/${templateId}`);
  if (response?.data?.success === false) {
    alert(response.data.message);
    return false;
  }
  return response?.data || false;
}

// Update client settings
async function updateClientSettings(data) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.put(`/client/settings/${client_id}`, data);
  if (response?.data?.success) return true;
  else {
    alert(response?.data?.message || 'Error updating settings');
    return false;
  }
}

// Generate an audio preview of a custom pronunciation spelling
async function previewPronunciation(text) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/client/pronunciation-preview`, { text }).catch(err => err.response);
  if (response?.data?.success) return response.data.audio;
  else {
    alert(response?.data?.message || 'Error generating audio preview');
    return false;
  }
}

// Run manual outbound call
async function runManualOutbound(data) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/outbound/manual`, data);
  return response?.data || { success: false, message: 'Error running manual outbound call' };
}

async function previewPractitionerAbsence(data) {
  const response = await axios.post(`${url}/outbound/practitioner-absence/preview`, data);
  return response?.data || { success: false, patients: [] };
}

async function runPractitionerAbsence(data) {
  const response = await axios.post(`${url}/outbound/practitioner-absence/send`, data);
  return response?.data || { success: false, message: 'Error starting practitioner diary campaign' };
}

// Check integrations
async function checkIntegrations() {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.get(`/client/integrations`);
  console.log(response.data)
  return response?.data?.integrations || false;
}

// Update integrations
async function updateIntegrations(integrations) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/client/integrations`, integrations).catch(err => console.log(err));
  if (response?.data?.success) return response?.data;
  else {
    return false;
  }
}

// Get waiting list entries
async function getWaitingListEntries() {
  const response = await axios.get(`${url}/outbound/waiting-list`);
  if (response?.data?.success === false) {
    alert(response.data.message);
    return [];
  }
  return response?.data || [];
}

// Add waiting list entry
async function addWaitingListEntry(entryData) {
  const response = await axios.post(`${url}/outbound/waiting-list`, entryData);
  if (response?.data?.success === false) {
    alert(response.data.message);
    return false;
  }
  return response?.data || false;
}

// Delete waiting list entry
async function deleteWaitingListEntry(entryId) {
  const response = await axios.delete(`${url}/outbound/waiting-list/${entryId}`);
  if (response?.data?.success === false) {
    alert(response.data.message);
    return false;
  }
  return response?.data || false;
}

// Refresh HighLevel
async function refreshHighLevel(instance_id) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/client/refresh-highlevel`, { instance_id });
  return response?.data?.pipelines || { success: false, message: 'Error refreshing HighLevel' };
}

// Get subscription billing options
async function getSubscriptionOptions() {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.get(`/client/subscription-options`);
  return response?.data || false;
}

// Create a payment
async function createPayment(billingInterval = 'monthly', resubscribe = false) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/client/create-payment`, { billingInterval, resubscribe });
  if (response?.data?.url) window.location.href = response.data.url;
  else {
    alert(response?.data?.message || 'Error starting payment. Please contact us to continue.');
    return false;
  }
}

// Connect Stripe account
async function connectStripe(code, state) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/stripe/connect`, { code, state });
  return response?.data || { status: 500 };
}

// Get availabilities
async function getAvailabilities(refresh = false) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.get(`/client/availabilities?refresh=${refresh}`);
  return response?.data?.availabilities || false;
}

// Update availabilities
async function updateAvailabilities(availabilities) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/client/availabilities`, { availabilities });
  if (response?.data?.success) return true;
  else {
    alert(response?.data?.message || 'Error updating settings');
    return false;
  }
}

// Request access to AI
async function requestAIAccess(feature) {
  const instance = axios.create({ baseURL: `${url}` });
  const data = { feature };
  const response = await instance.post(`/client/ai/request`, data);
  if (response?.data?.success) return true;
  else {
    alert(response?.data?.message || 'Error requesting access');
    return false;
  }
}

async function getMessages() {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.get(`/admin/messages`).catch(err => console.log(err));
  console.log(response)
  return response.data.messages;
}

async function addData(message, model) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/admin/data`, { message, model });
  return response.data.success;
}

async function getData() {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.get(`/admin/data`);
  console.log(response.data)
  return response.data.data;
}

async function removeData(data) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.put(`/admin/data`, { data });
  return response.data.data;
}

async function removeMessageFromData(data) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.put(`/admin/data/message`, data);
  return response.data.data;
}

/***********  Messaging functions ***********/

// Get dashboard data
async function getDashboard() {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.get(`/homepage/dashboard`).catch(err => {
    console.log(err.response.data)
    if (err.response.status === 401) window.location.href = '/login';
    else return [];
  });
  console.log(response);
  if (response.data.success === false) alert(response.data.message);
  else return response.data.data;
}

// Get account performance suggestions for the dashboard
async function getPerformanceSuggestions() {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.get(`/homepage/performance-suggestions`).catch(err => {
    console.log(err.response?.data);
    return { data: { success: false, suggestions: [] } };
  });
  return response.data || { success: false, suggestions: [] };
}

// Get all conversations for a client
async function getConversations(page, search = '') {
  const instance = axios.create({ baseURL: `${url}` });
  const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
  const response = await instance.get(`/ai/messaging/conversations?page=${page}${searchParam}`).catch(err => {
    console.log(err.response.data)
    if (err.response.status === 401) window.location.href = '/login';
    else return [];
  });
  console.log(response);
  if (response.data.success === false) alert(response.data.message);
  else return response.data;
}

// Get a single conversation
async function getConversation(contact, method) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.get(`/ai/messaging/conversations/${contact}/method/${method}`);
  console.log(response);
  if (response.data.success === false) alert(response.data.message);
  else return response.data.messages;
}

// Send a message
async function sendMessage(data) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/ai/messaging/send`, data);
  console.log(response);
  if (response.data.success === false) {
    alert(response.data.message);
    return false
  } else return true;
}

// Save an admin reply directly into a widget conversation (for polling by the widget)
async function saveWidgetAdminMessage(data) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/ai/widget/admin-reply`, data);
  if (response.data.success === false) {
    console.error('Failed to push message to widget:', response.data.message);
    return false;
  }
  return true;
}

// Unpause an account
async function unpauseAccount(data) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/ai/messaging/unpause`, data);
  console.log(response);
  if (response.data.success === false) {
    alert(response.data.message);
    return false
  } else return true;
}

// Get call history
async function getCalls(page, mode = 'inbound', search = '') {
  const instance = axios.create({ baseURL: `${url}` });
  const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
  const response = await instance.get(`/ai/voice/calls?page=${page}&mode=${mode}${searchParam}`).catch(err => {
    console.log(err.response.data)
    if (err.response.status === 401) window.location.href = '/login';
    else return [];
  });
  console.log(response);
  if (response.data.success === false) alert(response.data.message);
  else return response.data;
}

// Get call history
async function getCallById(id) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.get(`/ai/voice/calls/${id}`).catch(err => {
    console.log(err.response.data)
    if (err.response.status === 401) window.location.href = '/login';
    else return [];
  });
  console.log(response);
  if (response.data.success === false) alert(response.data.message);
  else return response.data;
}

// Mark a call as read
async function markCallRead(id) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/ai/voice/read`, { id });
  console.log(response);
  if (response.data.success === false) {
    alert(response.data.message);
    return false
  } else return true;
}

// Like a call
async function likeCall(id) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/ai/voice/like-call`, { id });
  console.log(response);
  if (response.data.success === false) {
    alert(response.data.message);
    return false
  } else return true;
}

// Dislike a call
async function dislikeCall(id, feedback = '') {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/ai/voice/dislike-call`, { id, feedback });
  console.log(response);
  if (response.data.success === false) {
    alert(response.data.message);
    return false
  } else return true;
}

// Update call outcome (complete/incomplete)
async function updateCallOutcome(id, outcome) {
  const instance = axios.create({ baseURL: `${url}` });

  try {
    const response = await instance.post(`/ai/voice/update-outcome`, { id, outcome });
    if (response.data.success === false) {
      alert(response.data.message);
      return false;
    }
    return response.data;
  } catch (error) {
    alert(error?.response?.data?.message || 'Error updating call outcome');
    return false;
  }
}

/***********  Info functions ***********/

// Get all faqs
async function getInfo() {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.get(`/info`);
  console.log(response);
  if (response.data.success === false) alert(response.data.message);
  else return response.data.data;
}

// Add an faq
async function addInfo(data) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/info`, data);
  console.log(response);
  if (response.data.success === false) {
    alert(response.data.message);
    return false
  } else return true;
}

// Upload a knowledge base file
async function uploadInfo(formData) {
  const instance = axios.create({
    baseURL: `${url}`,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const response = await instance.post(`/info/upload`, formData);
  console.log(response);
  if (response.data.success === false) {
    alert(response.data.message);
    return false
  } else return true;
}

// Delete an faq
async function deleteInfo(info_id) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.delete(`/info/${info_id}`);
  console.log(response);
  if (response.data.success === false) {
    alert(response.data.message);
    return false
  } else return true;
}

// Refresh a website source
async function refreshInfo(info_id) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/info/${info_id}/refresh`);
  console.log(response);
  if (response.data.success === false) {
    alert(response.data.message);
    return false
  } else return true;
}

// Get knowledge gaps
async function getKnowledgeGaps() {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.get(`/info/knowledge-gaps`);
  console.log(response);
  if (response.data.success === false) alert(response.data.message);
  else return response.data.gaps;
}

// Dismiss a knowledge gap
async function dismissKnowledgeGap(id) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.delete(`/info/knowledge-gaps/${id}`);
  console.log(response);
  if (response.data.success === false) {
    alert(response.data.message);
    return false;
  } else return true;
}

// Generate FAQs from knowledge base
async function generateInfo(data) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/info/generate`, data).catch(err => {
    console.log(err);
    alert('Sorry, there was an error generating data. Please try again later.');
    return false;
  });
  console.log(response);
  if (!response || response.data.success === false) {
    return false
  } else return true;
}

/***********  Channel Functions ***********/

// Get WhatsApp instance
async function getInstance() {
  const response = await axios.get(`${url}/channels/whatsapp`);
  return response.data;
}

// Create a new WhatsApp instance
async function createInstance() {
  const response = await axios.post(`${url}/channels/whatsapp`, {}).catch(err => {
    console.log(err);
    alert('Error creating WhatsApp instance. Please try again or contact support.');
  });
  return response.data;
}

// Delete a WhatsApp instance
async function disconnectInstance() {
  const response = await axios.delete(`${url}/channels/whatsapp`).catch(err => {
    console.log(err);
    alert('Error disconnecting WhatsApp instance. Please try again or contact support.');
  });
  return response.data;
}

// Provision a new phone number
async function provisionNumber(formData) {
  const instance = axios.create({
    baseURL: `${url}`,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const response = await instance.post(`/channels/provision-number`, formData).catch(err => {
    console.log(err);
    alert('Error requesting phone number. Please try again or contact support.');
  });
  return response.data;
}

// Connect WhatsApp Channel
async function enableWhatsApp(data) {
  const response = await axios.post(`${url}/channels/enable/whatsapp`, data).catch(err => {
    console.log(err);
    alert('Error requesting WhatsApp instance. Please try again or contact support.');
  });
  return response.data;
}

// Connect Email Inbox
async function connectEmailInbox(email_address) {
  const response = await axios.post(`${url}/channels/connect_inbox`, { email_address }).catch(err => {
    console.log(err);
    alert('Error connecting email inbox. Please try again or contact support.');
  });
  return response.data;
}

// Get all phone numbers
async function getPhoneNumbers() {
  const response = await axios.get(`${url}/channels/phone-numbers`).catch(err => {
    console.log(err);
  });
  return response.data;
}

// Assign location to phone number
async function assignLocationToNumber(numberId, location) {
  const response = await axios.put(`${url}/channels/phone-numbers/${numberId}/location`, { location }).catch(err => {
    console.log(err);
    alert('Error assigning location. Please try again or contact support.');
  });
  return response.data;
}

// Delete phone number
async function deletePhoneNumber(numberId) {
  const response = await axios.delete(`${url}/channels/phone-numbers/${numberId}`).catch(err => {
    console.log(err);
    alert('Error deleting phone number. Please try again or contact support.');
  });
  return response.data;
}

/***********  Notification functions ***********/

// Get notifications
async function getNotifications(archived = false) {
  try {
    const response = await axios.get(`${url}/homepage/notifications`, { params: { archived } });
    return response.data;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    return { success: false, error: error.message, notifications: [] };
  }
}

async function updateNotification(notificationId, updates) {
  try {
    const response = await axios.patch(`${url}/homepage/notifications/${notificationId}`, updates);
    return response.data;
  } catch (error) {
    console.error('Error updating notification:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
}

async function submitIntegrationRequest(pmsName) {
  try {
    const response = await axios.post(`${url}/homepage/integration-request`, { pmsName });
    return response.data;
  } catch (error) {
    console.error('Error submitting integration request:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
}

// Mark notification as read
async function markNotificationRead(notificationId) {
  try {
    const response = await axios.post(`${url}/homepage/notifications/read`, { notificationId });
    return response.data;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return { success: false, error: error.message };
  }
}

// Mark all notifications as read
async function markAllNotificationsRead() {
  try {
    const response = await axios.post(`${url}/homepage/notifications/read-all`, {});
    return response.data;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    return { success: false, error: error.message };
  }
}

// Toggle notification completion status
async function toggleNotificationCompleted(notificationId) {
  try {
    const response = await axios.post(`${url}/homepage/notifications/toggle-completed`, { notificationId });
    return response.data;
  } catch (error) {
    console.error('Error toggling notification completion:', error);
    return { success: false, error: error.message };
  }
}

async function searchClients(searchQuery) {
  try {
    const response = await axios.get(`${url}/admin/clients/search`, {
      params: { searchQuery }
    });
    return response.data;
  } catch (error) {
    console.error('Error searching clients:', error.response ? error.response.data : error.message);
    return { success: false, error: error.response ? error.response.data.message : error.message, clients: [] };
  }
}

async function createClientNotification(notificationData) {
  try {
    const response = await axios.post(`${url}/admin/clients/notification`, notificationData);
    return response.data;
  } catch (error) {
    console.error('Error creating client notification:', error.response ? error.response.data : error.message);
    return { success: false, error: error.response ? error.response.data.message : error.message };
  }
}

/***********  Request Log functions ***********/

// List request-log tickets (active or archived)
async function getRequestTickets(archived = false, options = {}) {
  try {
    const response = await axios.get(`${url}/admin/request-tickets`, {
      params: { archived, ...options },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching request tickets:', error.response ? error.response.data : error.message);
    return { success: false, error: error.message, tickets: [] };
  }
}

// Manually create a request-log ticket
async function createRequestTicket(data) {
  try {
    const response = await axios.post(`${url}/admin/request-tickets`, data);
    return response.data;
  } catch (error) {
    console.error('Error creating request ticket:', error.response ? error.response.data : error.message);
    return { success: false, error: error.response ? error.response.data.message : error.message };
  }
}

// Update a ticket's status and/or notes
async function updateRequestTicket(id, data) {
  try {
    const response = await axios.put(`${url}/admin/request-tickets/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating request ticket:', error.response ? error.response.data : error.message);
    return { success: false, error: error.response ? error.response.data.message : error.message };
  }
}

// Notify a client about a ticket
async function notifyRequestTicket(id, data) {
  try {
    const response = await axios.post(`${url}/admin/request-tickets/${id}/notify`, data);
    return response.data;
  } catch (error) {
    console.error('Error notifying client:', error.response ? error.response.data : error.message);
    return { success: false, error: error.response ? error.response.data.message : error.message };
  }
}

// Archive or restore a ticket
async function archiveRequestTicket(id, archived = true) {
  try {
    const response = await axios.put(`${url}/admin/request-tickets/${id}/archive`, { archived });
    return response.data;
  } catch (error) {
    console.error('Error archiving request ticket:', error.response ? error.response.data : error.message);
    return { success: false, error: error.response ? error.response.data.message : error.message };
  }
}

// Generate a suggested custom instruction for a request
async function generateCustomInstruction(request) {
  try {
    const response = await axios.post(`${url}/admin/generate-custom-instruction`, { request });
    return response.data;
  } catch (error) {
    console.error('Error generating custom instruction:', error.response ? error.response.data : error.message);
    return { success: false, error: error.response ? error.response.data.message : error.message };
  }
}

// Get statistics with time range and method filter
async function getStatistics(timeRangeStart, timeRangeEnd, method = '') {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/homepage/statistics`, {
    timeRangeStart,
    timeRangeEnd,
    method
  });
  return response?.data || { success: false };
}

/***********  Script functions ***********/

async function getScripts() {
  const response = await axios.get(`${url}/script`);
  return response.data;
}

async function saveScript(script, workflow) {
  const response = await axios.put(`${url}/script/${encodeURIComponent(script)}`, { workflow });
  return response.data;
}

async function deleteScript(script) {
  const response = await axios.delete(`${url}/script/${encodeURIComponent(script)}`);
  return response.data;
}

async function updateScriptsWithAI(data) {
  const response = await axios.post(`${url}/script/assistant`, data);
  return response.data;
}

/***********  Login Management Functions ***********/ // Get all logins for the client account
async function getLogins() {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.get(`/login/logins`);
  if (response.data.success === false) {
    alert(response.data.message);
    return false;
  } else return response.data.logins;
}

async function getAccessLogs(params = {}) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.get(`/login/access-logs`, { params });
  if (response.data.success === false) {
    alert(response.data.message);
    return false;
  } else return response.data;
}

async function terminateActiveSessions() {
  const response = await axios.delete(`${url}/login/sessions`);
  return response.data;
}

// Create a new login for the client account
async function createLogin(loginData) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/login/logins`, loginData);
  if (response.data.success === false) {
    return {
      success: false,
      message: response?.data?.message || 'Error creating login. Please try again later.',
    };
  } else return {
    success: true,
    message: 'Login created successfully.'
  };
}

// Update an existing login
async function updateLogin(loginId, loginData) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.put(`/login/logins/${loginId}`, loginData);
  if (response.data.success === false) {
    alert(response.data.message);
    return false;
  } else return response.data;
}

// Delete a login
async function deleteLogin(loginId) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.delete(`/login/logins/${loginId}`);
  if (response.data.success === false) {
    alert(response.data.message);
    return false;
  } else return response.data;
}

// Export statistics as CSV
async function exportStats(startDate, endDate) {
  try {
    const instance = axios.create({
      responseType: 'blob', // Important for file download
    });

    const response = await instance.post(`${url}/homepage/export-stats`, {
      startDate,
      endDate
    });

    // Create download link
    const blob = new Blob([response.data], { type: 'text/csv' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;

    // Get filename from response headers or create default
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'call-stats.csv';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return { success: true };
  } catch (error) {
    console.error('Error exporting stats:', error);
    alert(error?.response?.data?.message || 'Error exporting statistics');
    return { success: false, error: error.message };
  }
}

// Process AI helper request
async function processAIHelperRequest(messages, chatId) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/helper/request`, { messages, chat_id: chatId });
  if (response?.data?.success === false) {
    throw new Error(response.data.message || 'AI helper request failed');
  }
  return response?.data || { success: false, message: 'Error processing AI helper request' };
}

// Get all helper chats (admin only)
async function getHelperChats(page = 1) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.get(`/admin/helper/chats`, { params: { page } });
  return response?.data || { success: false, chats: [] };
}

// Get a single helper chat with decrypted content (admin only)
async function getHelperChatDetail(chatId) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.get(`/admin/helper/chat/${chatId}`);
  return response?.data || { success: false };
}

// Ask AI about a specific call
async function askAboutCall(callId, messages) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/helper/ask-about-call`, { callId, messages });
  if (response?.data?.success === false) {
    throw new Error(response.data.message || 'Failed to analyze call');
  }
  return response?.data || { success: false, message: 'Error analyzing call' };
}

// Process a knowledge base test request
async function processKbTestRequest(messages) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/helper/kb-test`, { messages });
  if (response?.data?.success === false) {
    throw new Error(response.data.message || 'Knowledge base test request failed');
  }
  return response?.data || { success: false, message: 'Error processing request' };
}

/***********  Two-Factor Authentication functions ***********/

// Send 2FA verification code
async function send2FACode(userId, userType, email, deviceFingerprint, challengeToken) {
  const data = {
    userId,
    userType,
    email,
    deviceFingerprint,
    challengeToken
  };

  try {
    const response = await axios.post(`${url}/login/2fa/send-code`, data);
    return response.data;
  } catch (error) {
    console.error('Error sending 2FA code:', error);
    throw error;
  }
}

// Verify 2FA code
async function verify2FACode(userId, userType, code, deviceFingerprint, rememberDevice, challengeToken) {
  const data = {
    userId,
    userType,
    code,
    deviceFingerprint,
    rememberDevice,
    challengeToken
  };

  try {
    const response = await axios.post(`${url}/login/2fa/verify-code`, data);
    return response.data;
  } catch (error) {
    console.error('Error verifying 2FA code:', error);
    throw error;
  }
}

/***********  Admin Tracking Functions ***********/

// Get all active clients with tracking data
// options: { range: '7d' | '30d' | 'mtd' | 'ytd' | 'custom', startDate, endDate }
async function getActiveClients(options = {}) {
  try {
    const instance = axios.create({ baseURL: `${url}` });
    const response = await instance.get(`/admin/tracking/clients`, { params: options });
    return response.data;
  } catch (error) {
    console.error('Error fetching active clients:', error);
    throw error;
  }
}

// Get detailed metrics for a specific client
async function getClientMetrics(clientId, period = 30) {
  try {
    const instance = axios.create({ baseURL: `${url}` });
    const response = await instance.get(`/admin/tracking/client/${clientId}?period=${period}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching client metrics:', error);
    throw error;
  }
}

// Get client details for admin editing
async function getClientForAdmin(clientId) {
  try {
    const instance = axios.create({ baseURL: `${url}` });
    const response = await instance.get(`/admin/client/${clientId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching client for admin:', error);
    throw error;
  }
}

// Get client details for admin editing
async function loginAsClient(clientId) {
  try {
    const instance = axios.create({ baseURL: `${url}` });
    const response = await instance.post(`/admin/client/${clientId}/login`, {});
    return response.data;
  } catch (error) {
    console.error('Error fetching client for admin:', error);
    throw error;
  }
}

// Update client custom instructions
async function updateClientInstructions(clientId, instructionType, instructions) {
  try {
    const instance = axios.create({ baseURL: `${url}` });
    const response = await instance.put(`/admin/client/${clientId}/instructions`, {
      instructionType,
      instructions
    });
    return response.data;
  } catch (error) {
    console.error('Error updating client instructions:', error);
    throw error;
  }
}

async function updateClientAdminSettings(clientId, settings) {
  try {
    const instance = axios.create({ baseURL: `${url}` });
    const response = await instance.put(`/admin/client/${clientId}/settings`, settings);
    return response.data;
  } catch (error) {
    console.error('Error updating client admin settings:', error);
    throw error;
  }
}

// Provision phone number to client (admin)
async function provisionNumberToClient(clientId, phoneNumberId, number, hasSms, location) {
  try {
    const instance = axios.create({ baseURL: `${url}` });
    const response = await instance.post(`/admin/client/${clientId}/provision-number`, {
      phoneNumberId,
      number,
      hasSms,
      location
    });
    return response.data;
  } catch (error) {
    console.error('Error provisioning number to client:', error);
    throw error;
  }
}

// Activate pending phone number (admin)
async function activatePendingNumber(phoneNumberId, active, notifyClient, number, location, has_sms) {
  try {
    const instance = axios.create({ baseURL: `${url}` });
    const response = await instance.put(`/admin/phone-number/${phoneNumberId}/activate`, {
      active,
      notifyClient,
      number,
      location,
      has_sms
    });
    return response.data;
  } catch (error) {
    console.error('Error activating phone number:', error);
    throw error;
  }
}

async function testIntegration(clientId) {
  try {
    const instance = axios.create({ baseURL: `${url}` });
    const response = await instance.post(`/admin/client/${clientId}/test-integration`);
    return response.data;
  } catch (error) {
    console.error('Error testing integration:', error);
    throw error;
  }
}

async function runIntegrationFunction(clientId, functionName, params, fromNumber) {
  try {
    const response = await axios.post(`${url}/admin/client/${clientId}/run-function`, {
      functionName,
      params,
      fromNumber,
    });
    return response.data;
  } catch (error) {
    return { success: false, message: error?.response?.data?.message || error.message };
  }
}

async function sendDetailsNeededEmail(clientId, details) {
  try {
    const instance = axios.create({ baseURL: `${url}` });
    const response = await instance.post(`/admin/client/${clientId}/send-details-needed`, { details });
    return response.data;
  } catch (error) {
    console.error('Error sending details needed email:', error);
    throw error;
  }
}

// Permanently wipe a client's sensitive account/integration data and release their Twilio number(s).
// Requires the requesting admin's own password and the client's email to be typed as confirmation.
async function deleteClientAccountData(clientId, password, confirmEmail) {
  try {
    const response = await axios.post(`${url}/admin/client/${clientId}/delete-account`, { password, confirmEmail });
    return response.data;
  } catch (error) {
    return { success: false, message: error?.response?.data?.message || error.message };
  }
}

// Delete a client's calls, messages and conversations only (account/integration data is untouched).
// Requires the requesting admin's own password and the client's email to be typed as confirmation.
async function deleteClientCommunicationData(clientId, password, confirmEmail) {
  try {
    const response = await axios.post(`${url}/admin/client/${clientId}/delete-communication-data`, { password, confirmEmail });
    return response.data;
  } catch (error) {
    return { success: false, message: error?.response?.data?.message || error.message };
  }
}

async function loginWithGoogle(credential) {
  const response = await axios.post(`${url}/login/google`, { credential })
    .catch((err) => { alert(err?.response?.data?.message || 'Google login failed. Please try again.'); });
  if (response?.status === 200) {
    localStorage.name = response.data.name || '';
    localStorage.email = response.data.email;
    localStorage.client_id = response.data.client_id;
    client_id = response.data.client_id;
    localStorage.isAdmin = response.data.isAdmin || false;
    localStorage.removeItem('isv_id');
    localStorage.registrationDate = response.data.registrationDate;
    window.dispatchEvent(new Event('settings_updated'));
    return response.data;
  }
  return null;
}

async function completeGoogleSignup(data) {
  const instance = axios.create({ baseURL: `${url}` });
  const response = await instance.post(`/client/complete-google-signup`, data)
    .catch((err) => { alert(err?.response?.data?.message || 'Failed to save details. Please try again.'); });
  return response?.data?.success || false;
}

// Switch the active subaccount (ISV only) — updates the adminSession cookie server-side
async function switchAccount(clientId) {
  const response = await axios.post(`${url}/isv/switch`, { clientId });
  return response?.data || { success: false };
}

// Exchange a raw JWT token from URL params for an httpOnly session cookie
async function activateToken(token, clientId) {
  const response = await axios.post(`${url}/login/activate-token`, { token, client_id: clientId });
  return response?.data || { success: false };
}

// Logout — invalidates the token and clears session cookies
async function logout() {
  try {
    await axios.post(`${url}/login/logout`);
  } catch (e) { /* best-effort */ }
  const nonSensitiveKeys = ['name', 'email', 'client_id', 'isAdmin', 'isv_id', 'registrationDate',
    'settings', 'navEnlarge', 'hasOutbound'];
  nonSensitiveKeys.forEach(k => localStorage.removeItem(k));
  window.location.href = '/login';
}

export {
  login,
  getISV,
  addClient,
  register,
  getReferralSourcePrompt,
  updateReferralSourcePrompt,
  sendFeedback,
  forgotPassword,
  resetPassword,
  getClientSettings,
  getCompliancePreset,
  getOutboundStats,
  getOutboundInstances,
  createOutboundInstance,
  updateOutboundInstance,
  deleteOutboundInstance,
  getCampaignTemplates,
  createCampaignTemplate,
  updateCampaignTemplate,
  deleteCampaignTemplate,
  updateClientSettings,
  previewPronunciation,
  runManualOutbound,
  previewPractitionerAbsence,
  runPractitionerAbsence,
  checkIntegrations,
  updateIntegrations,
  getWaitingListEntries,
  addWaitingListEntry,
  deleteWaitingListEntry,
  refreshHighLevel,
  getSubscriptionOptions,
  createPayment,
  connectStripe,
  getAvailabilities,
  updateAvailabilities,
  requestAIAccess,
  getMessages,
  addData,
  getData,
  removeData,
  removeMessageFromData,
  getDashboard,
  getPerformanceSuggestions,
  getConversations,
  getConversation,
  sendMessage,
  saveWidgetAdminMessage,
  unpauseAccount,
  getCalls,
  getCallById,
  markCallRead,
  likeCall,
  dislikeCall,
  updateCallOutcome,
  getInfo,
  addInfo,
  uploadInfo,
  deleteInfo,
  refreshInfo,
  getKnowledgeGaps,
  dismissKnowledgeGap,
  generateInfo,
  getInstance,
  createInstance,
  disconnectInstance,
  provisionNumber,
  enableWhatsApp,
  connectEmailInbox,
  getPhoneNumbers,
  assignLocationToNumber,
  deletePhoneNumber,
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  toggleNotificationCompleted,
  updateNotification,
  submitIntegrationRequest,
  searchClients,
  createClientNotification,
  deleteClientAccountData,
  deleteClientCommunicationData,
  getRequestTickets,
  createRequestTicket,
  updateRequestTicket,
  notifyRequestTicket,
  archiveRequestTicket,
  generateCustomInstruction,
  getStatistics,
  getScripts,
  saveScript,
  deleteScript,
  updateScriptsWithAI,
  getLogins,
  getAccessLogs,
  terminateActiveSessions,
  createLogin,
  updateLogin,
  deleteLogin,
  exportStats,
  processAIHelperRequest,
  getHelperChats,
  getHelperChatDetail,
  askAboutCall,
  processKbTestRequest,
  send2FACode,
  verify2FACode,
  getActiveClients,
  getClientMetrics,
  getClientForAdmin,
  loginAsClient,
  updateClientInstructions,
  updateClientAdminSettings,
  provisionNumberToClient,
  activatePendingNumber,
  testIntegration,
  runIntegrationFunction,
  sendDetailsNeededEmail,
  loginWithGoogle,
  completeGoogleSignup,
  switchAccount,
  logout,
  activateToken,
};